import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  FormHelperText,
  MenuItem,
  OutlinedInput,
  InputAdornment,
  Popover,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress,
  Card
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import palette from 'src/theme/palette';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { getStates, getCities } from 'src/ApiServices';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import useMediaQuery from '@mui/material/useMediaQuery';

const AddCitiesModal = ({ open, onClose, onSuccess, city }) => {
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedCityName, setSelectedCityName] = useState('');
  const [selectedStateName, setSelectedStateName] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [slug, setSlug] = useState('');
  const [slugError, setSlugError] = useState('');
  const [isSlugEdited, setIsSlugEdited] = useState(false);
  const [statesLoading, setStatesLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [statesSearch, setStatesSearch] = useState('');
  const [citiesSearch, setCitiesSearch] = useState('');
  const [statesAnchorEl, setStatesAnchorEl] = useState(null);
  const [citiesAnchorEl, setCitiesAnchorEl] = useState(null);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [submitError, setSubmitError] = useState('');
  const isMobile = useMediaQuery('(max-width:600px)');

  const fileInputRef = useRef(null);

  // Rich Text Editor modules configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  };

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'script',
    'list',
    'bullet',
    'indent',
    'align',
    'blockquote',
    'code-block',
    'link',
    'image',
    'video'
  ];

  const filteredStates = useMemo(() => {
    if (!Array.isArray(states)) return [];

    if (!statesSearch.trim()) return states;

    return states.filter((state) => state.name.toLowerCase().includes(statesSearch.toLowerCase()));
  }, [states, statesSearch]);

  const filteredCities = useMemo(() => {
    if (!Array.isArray(cities)) return [];

    if (!citiesSearch.trim()) return cities;

    return cities.filter((cityItem) =>
      cityItem.name.toLowerCase().includes(citiesSearch.toLowerCase())
    );
  }, [cities, citiesSearch]);

  const handleStateSelect = (stateId) => {
    const selectedStateData = states.find((state) => state.id === stateId);
    setSelectedState(stateId);
    setSelectedStateName(selectedStateData?.name || '');
    setStatesAnchorEl(null);
    setStatesSearch('');

    setSelectedCity('');
    setSelectedCityName('');
    setCities([]);

    if (!city) {
      setSlug('');
      setIsSlugEdited(false);
    }
  };

  const handleCitySelect = (cityId) => {
    setSelectedCity(cityId);
    setCitiesAnchorEl(null);
    setCitiesSearch('');

    const selectedCityData = cities.find((city) => city.id === cityId);
    if (selectedCityData) {
      setSelectedCityName(selectedCityData.name);
    } else {
      setSelectedCityName('');
    }

    if (!city) {
      setIsSlugEdited(false);
    }
  };

  // Open states popover
  const handleStatesInputClick = (event) => {
    setStatesAnchorEl(event.currentTarget);
  };

  // Open cities popover
  const handleCitiesInputClick = (event) => {
    setCitiesAnchorEl(event.currentTarget);
  };

  // Close states popover
  const handleCloseStates = () => {
    setStatesAnchorEl(null);
    setStatesSearch('');
  };

  // Close cities popover
  const handleCloseCities = () => {
    setCitiesAnchorEl(null);
    setCitiesSearch('');
  };

  // Clear states search
  const handleClearStatesSearch = () => {
    setStatesSearch('');
  };

  // Clear cities search
  const handleClearCitiesSearch = () => {
    setCitiesSearch('');
  };

  // Modify handleResetFilters if needed
  const handleClearAllSelections = () => {
    setSelectedStates([]);
    setSelectedCities([]);
  };

  useEffect(() => {
    if (open) {
      setInitialLoadComplete(false);
      setSubmitError('');
      setSubmitting(false);
      fetchStates();

      if (city) {
        const stateId = city.state?.id || '';
        setSelectedState(stateId);
        setSelectedStateName(city.state?.name || '');
        setDescription(city.description || '');
        setSlug(city.slug || '');
        setIsSlugEdited(true);

        if (city.image) {
          setImagePreview(city.image);
        }

        const cityId = city.city?.id || '';
        setSelectedCity(cityId);
        setSelectedCityName(city.city?.name || '');

        if (stateId) {
          fetchCities(stateId, cityId);
        }
      } else {
        // Reset form for new city
        setSelectedState('');
        setSelectedStateName('');
        setSelectedCity('');
        setSelectedCityName('');
        setSelectedImage(null);
        setImagePreview('');
        setDescription('');
        setDescriptionError('');
        setSlug('');
        setSlugError('');
        setIsSlugEdited(false);
        setStatesSearch('');
        setCitiesSearch('');
        setStatesAnchorEl(null);
        setCitiesAnchorEl(null);
        setSelectedStates([]);
        setSelectedCities([]);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
      setInitialLoadComplete(true);
    }
  }, [open, city]);

  useEffect(() => {
    if (selectedState && open && !city) {
      fetchCities(selectedState);
    }
  }, [selectedState, open, city]);

  useEffect(() => {
    // Auto-generate slug when both state and city are selected and not in edit mode
    if (selectedStateName && selectedCityName && !isSlugEdited && !city) {
      const generatedSlug = generateSlug(`${selectedStateName}-${selectedCityName}`);
      setSlug(generatedSlug);
    }
  }, [selectedStateName, selectedCityName, isSlugEdited, city]);

  const fetchStates = async () => {
    try {
      setStatesLoading(true);
      const response = await getStates();
      setStates(response.data || []);
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    } finally {
      setStatesLoading(false);
    }
  };

  const fetchCities = async (stateId, preSelectCityId = null) => {
    try {
      setCitiesLoading(true);
      const response = await getCities(stateId);
      const citiesList = response.data || [];
      setCities(citiesList);

      if (preSelectCityId && citiesList.length > 0) {
        const foundCity = citiesList.find((c) => c.id === preSelectCityId);
        if (foundCity) {
          setSelectedCity(foundCity.id);
          setSelectedCityName(foundCity.name);
        }
      } else if (city && city.city?.id && stateId === city.state?.id) {
        const foundCity = citiesList.find((c) => c.name === city.city?.name);
        if (foundCity) {
          setSelectedCity(foundCity.id);
          setSelectedCityName(foundCity.name);
        }
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
    } finally {
      setCitiesLoading(false);
    }
  };

  const generateSlug = (text) => {
    if (!text) return '';

    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/--+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  };

  const formatSlug = (input) => {
    return input
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/--+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  };

  const validateSlug = (slugValue) => {
    if (!slugValue.trim()) {
      return 'Slug is required';
    }
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slugValue)) {
      return 'Slug can only contain lowercase letters, numbers, and hyphens. No spaces or special characters allowed.';
    }
    if (slugValue.length < 3) {
      return 'Slug should be at least 3 characters long';
    }
    if (slugValue.length > 100) {
      return 'Slug should not exceed 100 characters';
    }
    return '';
  };

  const validateDescriptionField = (value) => {
    const strippedValue = value?.replace(/<[^>]*>/g, '').trim();
    return strippedValue ? '' : 'Description is required';
  };

  const handleSlugChange = (event) => {
    const inputSlug = event.target.value;
    setSlug(inputSlug);
    setIsSlugEdited(true);
    const formattedSlug = formatSlug(inputSlug);
    const error = validateSlug(formattedSlug);
    setSlugError(error);
  };

  const handleSlugBlur = (event) => {
    const inputSlug = event.target.value;
    const formattedSlug = formatSlug(inputSlug);
    setSlug(formattedSlug);
    const error = validateSlug(formattedSlug);
    setSlugError(error);
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      setSelectedImage(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleDescriptionChange = (value) => {
    setDescription(value);
    const error = validateDescriptionField(value);
    setDescriptionError(error);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    // Only clear the form when explicitly closing, not on submit errors
    setSelectedState('');
    setSelectedStateName('');
    setSelectedCity('');
    setSelectedCityName('');
    setSelectedImage(null);
    setImagePreview('');
    setDescription('');
    setDescriptionError('');
    setSlug('');
    setSlugError('');
    setIsSlugEdited(false);
    setStatesSearch('');
    setCitiesSearch('');
    setStatesAnchorEl(null);
    setCitiesAnchorEl(null);
    setSelectedStates([]);
    setSelectedCities([]);
    setSubmitError('');
    setSubmitting(false); // Add this line

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    onClose();
  };
  // Update the handleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);

    // Validate all fields
    const strippedDescription = description?.replace(/<[^>]*>/g, '').trim();
    const finalSlug = formatSlug(slug);
    const validSlug = finalSlug && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(finalSlug);

    let hasError = false;

    if (!selectedState) {
      toast.error('Please select a state');
      hasError = true;
    }
    if (!selectedCity) {
      toast.error('Please select a city');
      hasError = true;
    }
    if (!strippedDescription) {
      toast.error('Please enter description');
      hasError = true;
    }
    if (!validSlug) {
      toast.error('Please enter a valid slug');
      hasError = true;
    }
    if (!city && !selectedImage) {
      toast.error('Please upload an image');
      hasError = true;
    }

    if (hasError) {
      setSubmitting(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('state_id', selectedState);
      formData.append('city_id', selectedCity);
      formData.append('description', description);
      formData.append('slug', finalSlug);

      if (selectedImage) {
        formData.append('image', selectedImage);
      } else if (city?.image && !imagePreview) {
        formData.append('image', '');
      } else if (city?.image && imagePreview && imagePreview === city.image) {
        try {
          const response = await fetch(city.image);
          const blob = await response.blob();
          const fileName = city.image.split('/').pop() || 'current-image.jpg';
          const file = new File([blob], fileName, { type: blob.type });
          formData.append('image', file);
        } catch (error) {
          console.error('Error converting image to blob:', error);
        }
      }

      if (city && city.id) {
        formData.append('id', city.id);
      }

      // Call onSuccess and wait for response
      const response = await onSuccess(formData);

      // Check if response status is 200 or 201
      if (response && (response.status === 200 || response.status === 201)) {
        // Only show success message and close modal on success
        toast.success(city ? 'City updated successfully!' : 'City added successfully!');

        // Clear form and close modal
        setSelectedState('');
        setSelectedStateName('');
        setSelectedCity('');
        setSelectedCityName('');
        setSelectedImage(null);
        setImagePreview('');
        setDescription('');
        setDescriptionError('');
        setSlug('');
        setSlugError('');
        setIsSlugEdited(false);
        setStatesSearch('');
        setCitiesSearch('');
        setStatesAnchorEl(null);
        setCitiesAnchorEl(null);
        setSelectedStates([]);
        setSelectedCities([]);
        setSubmitError('');

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        onClose();
      } else {
        // Handle unexpected response
        setSubmitError('Failed to save city. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Show error in modal but don't close it
      setSubmitError(error.message || 'Failed to save city. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = () => {
    const strippedDescription = description?.replace(/<[^>]*>/g, '').trim();
    const finalSlug = formatSlug(slug);
    const validSlug = finalSlug && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(finalSlug);

    // Check required fields
    const hasValidState = !!selectedState;
    const hasValidCity = !!selectedCity;
    const hasValidDescription = !!strippedDescription;
    const hasValidSlug = validSlug;

    // For add mode, image is required
    if (!city && !selectedImage) {
      return false;
    }

    return hasValidState && hasValidCity && hasValidDescription && hasValidSlug;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth={!isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          margin: isMobile ? 0 : { xs: 1, sm: 3 },
          width: '100%',
          height: isMobile ? '100vh' : 'auto',
          maxHeight: isMobile ? '100vh' : '90vh'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 9
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontFamily: 'poppinsSemiBold',
            color: palette.primary.contrastText
          }}
        >
          {city ? 'Edit City' : 'Add New City'}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: palette.primary.dark
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            {submitError && (
              <Box
                sx={{
                  backgroundColor: palette.error.lighter,
                  color: palette.error.main,
                  p: 2,
                  borderRadius: 1,
                  mb: 3,
                  border: `1px solid ${palette.error.main}`
                }}
              >
                <Typography sx={{ fontSize: '0.875rem' }}>{submitError}</Typography>
              </Box>
            )}

            <Grid container spacing={3}>
              {/* State Dropdown with Search */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel
                    sx={{
                      color: palette.primary.title,
                      '&.Mui-focused': {
                        color: palette.primary.main
                      }
                    }}
                  >
                    {statesLoading ? 'Loading States...' : 'Select State'}
                  </InputLabel>
                  <OutlinedInput
                    value={selectedStateName}
                    label={statesLoading ? 'Loading States...' : 'Select State'}
                    readOnly
                    onClick={handleStatesInputClick}
                    disabled={statesLoading || submitting}
                    endAdornment={
                      <InputAdornment position="end">
                        {statesLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <IconButton
                            size="small"
                            edge="end"
                            onClick={handleStatesInputClick}
                            disabled={statesLoading || submitting}
                            sx={{ color: palette.grey[600] }}
                          >
                            {Boolean(statesAnchorEl) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                          </IconButton>
                        )}
                      </InputAdornment>
                    }
                    sx={{
                      cursor: 'pointer',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.primary.inputborder
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.primary.main
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.primary.main
                      },
                      color: palette.primary.title
                    }}
                  />
                  {statesLoading && (
                    <FormHelperText sx={{ color: palette.primary.title }}>
                      Loading states...
                    </FormHelperText>
                  )}
                </FormControl>

                {/* States Search Popover */}
                <Popover
                  open={Boolean(statesAnchorEl)}
                  anchorEl={statesAnchorEl}
                  onClose={handleCloseStates}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                  sx={{
                    '& .MuiPopover-paper': {
                      width: 400,
                      maxWidth: '100%',
                      maxHeight: 400,
                      borderRadius: 2,
                      boxShadow: `0 8px 32px rgba(0,0,0,0.2)`
                    }
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    {/* Search Input */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <OutlinedInput
                        placeholder="Search states..."
                        value={statesSearch}
                        onChange={(e) => setStatesSearch(e.target.value)}
                        size="small"
                        startAdornment={
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: palette.grey[600] }} />
                          </InputAdornment>
                        }
                        endAdornment={
                          statesSearch && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={handleClearStatesSearch}
                                sx={{ color: palette.grey[600] }}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )
                        }
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: palette.inputborder
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: palette.primary.main
                          }
                        }}
                      />
                    </FormControl>

                    {/* States List */}
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {statesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                          <CircularProgress size={24} />
                          <Typography sx={{ ml: 2, color: palette.grey[600] }}>
                            Loading states...
                          </Typography>
                        </Box>
                      ) : filteredStates.length > 0 ? (
                        <>
                          <MenuItem
                            value=""
                            onClick={() => handleStateSelect('')}
                            sx={{
                              color: palette.primary.title,
                              '&:hover': {
                                backgroundColor: palette.grey[50]
                              }
                            }}
                          >
                            <em>None</em>
                          </MenuItem>
                          {filteredStates.map((state) => (
                            <MenuItem
                              key={state.id}
                              value={state.id}
                              onClick={() => handleStateSelect(state.id)}
                              sx={{
                                color: palette.primary.title,
                                '&:hover': {
                                  backgroundColor: palette.grey[50]
                                }
                              }}
                            >
                              {state.name}
                            </MenuItem>
                          ))}
                        </>
                      ) : (
                        <Typography
                          sx={{
                            color: palette.grey[600],
                            textAlign: 'center',
                            p: 3,
                            fontSize: '0.875rem'
                          }}
                        >
                          {statesSearch ? 'No states found' : 'No states available'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Popover>
              </Grid>

              {/* City Dropdown with Search */}
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  disabled={!selectedState || citiesLoading || submitting}
                >
                  <InputLabel
                    sx={{
                      color: palette.primary.title,
                      '&.Mui-focused': {
                        color: palette.primary.main
                      }
                    }}
                  >
                    {citiesLoading ? 'Loading Cities...' : 'Select City'}
                  </InputLabel>
                  <OutlinedInput
                    value={selectedCityName}
                    label={citiesLoading ? 'Loading Cities...' : 'Select City'}
                    readOnly
                    onClick={handleCitiesInputClick}
                    disabled={!selectedState || citiesLoading || submitting}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={handleCitiesInputClick}
                          disabled={!selectedState || citiesLoading || submitting}
                          sx={{ color: palette.grey[600] }}
                        >
                          {Boolean(citiesAnchorEl) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                        </IconButton>
                      </InputAdornment>
                    }
                    sx={{
                      cursor: selectedState ? 'pointer' : 'not-allowed',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.primary.inputborder
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.primary.main
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.primary.main
                      },
                      color: palette.primary.title
                    }}
                  />
                  {!selectedState && (
                    <FormHelperText sx={{ color: palette.primary.title }}>
                      Please select a state first
                    </FormHelperText>
                  )}
                  {selectedState && cities.length === 0 && !citiesLoading && (
                    <FormHelperText sx={{ color: palette.warning.main }}>
                      No cities found for this state
                    </FormHelperText>
                  )}
                </FormControl>

                {/* Cities Search Popover */}
                <Popover
                  open={Boolean(citiesAnchorEl)}
                  anchorEl={citiesAnchorEl}
                  onClose={handleCloseCities}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                  sx={{
                    '& .MuiPopover-paper': {
                      width: 400,
                      maxWidth: '100%',
                      maxHeight: 400,
                      borderRadius: 2,
                      boxShadow: `0 8px 32px rgba(0,0,0,0.2)`
                    }
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    {/* Search Input */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <OutlinedInput
                        placeholder="Search cities..."
                        value={citiesSearch}
                        onChange={(e) => setCitiesSearch(e.target.value)}
                        size="small"
                        disabled={!selectedState}
                        startAdornment={
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: palette.grey[600] }} />
                          </InputAdornment>
                        }
                        endAdornment={
                          citiesSearch && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={handleClearCitiesSearch}
                                sx={{ color: palette.grey[600] }}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )
                        }
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: palette.inputborder
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: palette.primary.main
                          }
                        }}
                      />
                    </FormControl>

                    {/* Cities List */}
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {citiesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                          <CircularProgress size={24} />
                          <Typography sx={{ ml: 2, color: palette.grey[600] }}>
                            Loading cities...
                          </Typography>
                        </Box>
                      ) : filteredCities.length > 0 ? (
                        <>
                          <MenuItem
                            value=""
                            onClick={() => handleCitySelect('')}
                            sx={{
                              color: palette.primary.title,
                              '&:hover': {
                                backgroundColor: palette.grey[50]
                              }
                            }}
                          >
                            <em>None</em>
                          </MenuItem>
                          {filteredCities.map((cityItem) => (
                            <MenuItem
                              key={cityItem.id}
                              value={cityItem.id}
                              onClick={() => handleCitySelect(cityItem.id)}
                              sx={{
                                color: palette.primary.title,
                                '&:hover': {
                                  backgroundColor: palette.grey[50]
                                }
                              }}
                            >
                              {cityItem.name}
                            </MenuItem>
                          ))}
                        </>
                      ) : (
                        <Typography
                          sx={{
                            color: palette.grey[600],
                            textAlign: 'center',
                            p: 3,
                            fontSize: '0.875rem'
                          }}
                        >
                          {citiesSearch ? 'No cities found' : 'No cities available'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Popover>
              </Grid>

              {/* Slug Field */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    label="Slug"
                    value={slug}
                    onChange={handleSlugChange}
                    onBlur={handleSlugBlur}
                    disabled={submitting}
                    error={!!slugError}
                    helperText={
                      slugError ||
                      (formatSlug(slug) !== slug
                        ? `Formatted version: ${formatSlug(slug)}`
                        : 'You can type freely. Special characters will be automatically removed. Use spaces or hyphens between words.')
                    }
                    placeholder="e.g., california-los-angeles"
                    sx={{
                      backgroundColor: palette.background.paper,
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: slugError ? palette.error.main : palette.primary.main
                        }
                      }
                    }}
                  />
                  {selectedStateName && selectedCityName && !isSlugEdited && !city && (
                    <FormHelperText sx={{ color: palette.success.main, mt: 0.5 }}>
                      Auto-generated from state and city: "{selectedStateName} - {selectedCityName}"
                    </FormHelperText>
                  )}
                  {isSlugEdited && (
                    <FormHelperText sx={{ color: palette.info.main, mt: 0.5 }}>
                      {formatSlug(slug) === slug
                        ? 'Valid slug format'
                        : `Will be saved as: ${formatSlug(slug)}`}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Image Upload Section */}
              <Grid item xs={12}>
                <Card
                  sx={{
                    border: `2px dashed ${palette.primary.inputborder}`,
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: palette.background.paper,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: palette.primary.main,
                      backgroundColor: palette.primary.highlightBLue
                    }
                  }}
                >
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="modal-image-upload"
                    type="file"
                    onChange={handleImageChange}
                    disabled={submitting}
                    ref={fileInputRef}
                  />
                  <label htmlFor="modal-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={submitting}
                      sx={{
                        borderColor: palette.primary.inputborder,
                        color: palette.primary.title,
                        '&:hover': {
                          borderColor: palette.primary.main,
                          backgroundColor: palette.primary.hover
                        }
                      }}
                    >
                      {city ? 'Change Image' : 'Upload Image'}
                    </Button>
                  </label>

                  {(imagePreview || (city?.image && !selectedImage)) && (
                    <Box sx={{ mt: 2 }}>
                      <img
                        src={imagePreview || city?.image}
                        alt="Preview"
                        style={{
                          maxWidth: '200px',
                          maxHeight: '200px',
                          borderRadius: '8px',
                          marginBottom: '10px'
                        }}
                      />
                      <Box>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={handleRemoveImage}
                          disabled={submitting}
                          sx={{
                            borderColor: palette.error.main,
                            color: palette.error.main,
                            '&:hover': {
                              borderColor: palette.error.dark,
                              backgroundColor: palette.error.lighter
                            }
                          }}
                        >
                          {city ? 'Remove New Image' : 'Remove Image'}
                        </Button>
                      </Box>
                    </Box>
                  )}

                  <FormHelperText sx={{ color: palette.primary.title, mt: 1 }}>
                    {city
                      ? 'Click to change image (JPEG, PNG, etc.) - Max 5MB (Optional)'
                      : 'Click to upload an image (JPEG, PNG, etc.) - Max 5MB (Required)'}
                  </FormHelperText>

                  {selectedImage && (
                    <FormHelperText sx={{ color: palette.success.main }}>
                      Selected: {selectedImage.name}
                    </FormHelperText>
                  )}
                </Card>
              </Grid>

              {/* Rich Text Editor for Description */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.primary.titledark,
                    mb: 2
                  }}
                >
                  City Description
                </Typography>

                <Box
                  sx={{
                    border: descriptionError
                      ? `1px solid ${palette.error.main}`
                      : `1px solid ${palette.primary.inputborder}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                    '& .ql-toolbar': {
                      border: 'none',
                      borderBottom: `1px solid ${palette.primary.inputborder}`,
                      backgroundColor: palette.background.neutral,
                      '& .ql-picker-label': {
                        fontFamily: 'poppinsRegular'
                      },
                      '& .ql-picker-options': {
                        fontFamily: 'poppinsRegular'
                      }
                    },
                    '& .ql-container': {
                      border: 'none',
                      fontFamily: 'poppinsRegular',
                      fontSize: '14px',
                      minHeight: '300px'
                    },
                    '& .ql-editor': {
                      minHeight: '300px',
                      fontFamily: 'poppinsRegular',
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        fontFamily: 'poppinsSemiBold',
                        marginTop: '8px',
                        marginBottom: '8px'
                      },
                      '& a': {
                        color: palette.primary.main,
                        textDecoration: 'underline',
                        '&:hover': {
                          color: palette.primary.dark
                        }
                      },
                      '& img': {
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'all 0.3s ease',
                        maxWidth: '100%',
                        height: 'auto',
                        margin: '8px 0',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: `0 4px 12px ${palette.primary.main}40`
                        }
                      },
                      '&.ql-blank::before': {
                        color: palette.text.disabled,
                        fontStyle: 'normal',
                        fontFamily: 'poppinsRegular'
                      }
                    },
                    '&:hover': {
                      borderColor: palette.primary.main
                    },
                    '&:focus-within': {
                      borderColor: palette.primary.main,
                      borderWidth: 2
                    }
                  }}
                >
                  <ReactQuill
                    value={description}
                    onChange={handleDescriptionChange}
                    modules={modules}
                    formats={formats}
                    placeholder="Enter city description..."
                    theme="snow"
                  />
                </Box>

                {descriptionError && (
                  <FormHelperText
                    error
                    sx={{
                      ml: 0,
                      mt: 1
                    }}
                  >
                    {descriptionError}
                  </FormHelperText>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        {/* Footer Buttons */}
        <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={submitting}
            sx={{
              borderColor: palette.primary.inputborder,
              color: palette.primary.title,
              '&:hover': {
                borderColor: palette.primary.main,
                backgroundColor: palette.primary.hover
              }
            }}
          >
            Close
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !isFormValid()}
            sx={{
              backgroundColor: palette.primary.main,
              color: palette.primary.contrastText,
              '&:hover': {
                backgroundColor: palette.primary.dark
              },
              '&:disabled': {
                backgroundColor: palette.primary.disabled,
                color: palette.text.disabled
              }
            }}
          >
            {submitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                <span>Saving...</span>
              </Box>
            ) : (
              `${city ? 'Update' : 'Save'} City`
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddCitiesModal;
